import { useEffect, useRef, useState } from "react";
import {
  createPaymentMethod,
  deletePaymentMethod,
  getListPaymentMethod,
  updatePaymentMethod,
} from "../../api/paymentMethod";
import { useQuery } from "react-query";
import { isNumeric } from "../../utils";
import { IS_CARD_PAYMENT_TEXT, IS_NOT_CARD_PAYMENT_TEXT } from "../../constants";

const defaultPaymentMethod = {
  name: "",
  is_card_payment: false,
  is_color_red: false,
  remarks: "",
};

const usePaymentMethodMaster = () => {
  const [arrPaymentMethods, setArrPaymentMethods] = useState([]);
  const [activeSortColumn, setActiveSortColumn] = useState(0);
  const [sortColumnDirection, setSortColumnDirection] = useState("desc");
  const [sortedData, setSortedData] = useState([]);
  const [newPaymentMethod, setNewPaymentMethod] = useState({
    name: "",
    is_card_payment: false,
    is_color_red: false,
    remarks: "",
  });

  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);

  const [editPaymentMethod, setEditPaymentMethod] = useState(null);
  const [paymentMethodToDelete, setPaymentMethodToDelete] = useState(null);
  const [isPopupOpenUpdate, setIsPopupOpenUpdate] = useState(false);
  const [isPopupOpenDelete, setIsPopupOpenDelete] = useState(false);
  const [focusState, setFocusState] = useState({
    name: false,
    remarks: false,
  });

  const nameInputRef = useRef(null);

  const [errors, setErrors] = useState({
    name: "",
    remarks: "",
  });

  const [errorsUpdate, setErrorsUpdate] = useState({
    name: "",
    remarks: "",
  });

  const { data, refetch } = useQuery(
    ["paymentMethods", page, rowsPerPage],
    () => {
      const params = {
        page: page + 1,
        per_page: rowsPerPage,
      };
      return getListPaymentMethod(params);
    },
    {
      keepPreviousData: true,
    }
  );

  useEffect(() => {
    if (data) {
      setArrPaymentMethods(transformPaymentMethodData(data.data.data));
      setTotal(data.data.total);
    }
  }, [data]);

  useEffect(() => {
    setSortedData(arrPaymentMethods.sort((a, b) => b.id - a.id));
  }, [arrPaymentMethods]);

  useEffect(() => {
    if (nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, []);

  const transformPaymentMethodData = (data) => {
    return data.map((paymentMethod) => ({
      id: paymentMethod.id.toString(),
      name: paymentMethod.name,
      is_card_payment: paymentMethod.is_card_payment ? IS_CARD_PAYMENT_TEXT : IS_NOT_CARD_PAYMENT_TEXT,
      is_color_red: paymentMethod.is_color_red,
      remarks: paymentMethod.remarks || "",
    }));
  };

  const handleSort = (columnIndex) => {
    const isSorted = activeSortColumn === columnIndex;
    const direction = isSorted
      ? sortColumnDirection === "asc"
        ? "desc"
        : "asc"
      : "desc";
    const sorted = [...sortedData].sort((a, b) => {
      let aValue = a[Object.keys(a)[columnIndex]];
      let bValue = b[Object.keys(b)[columnIndex]];

      // if aValue and bValue are numberic, sort parseInt
      if (isNumeric(aValue) && isNumeric(bValue)) {
        aValue = parseInt(aValue);
        bValue = parseInt(bValue);
      }

      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });
    setSortedData(sorted);
    setActiveSortColumn(columnIndex);
    setSortColumnDirection(direction);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewPaymentMethod({ ...newPaymentMethod, [name]: value });
    setErrors({ name: "", remarks: "" });
  };

  const handleToggleCardPayment = () => {
    setNewPaymentMethod((prevData) => ({
      ...prevData,
      is_card_payment: !prevData.is_card_payment,
    }));
  }

  const handleToggleIsColorRed = () => {
    setNewPaymentMethod((prevData) => ({
      ...prevData,
      is_color_red: !prevData.is_color_red,
    }));
  }

  const handleAddPaymentMethod = async () => {
    try {
      const newPaymentMethodData = {
        name: newPaymentMethod.name,
        is_card_payment: newPaymentMethod.is_card_payment,
        is_color_red: newPaymentMethod.is_color_red,
        remarks: newPaymentMethod.remarks,
      };

      const response = await createPaymentMethod(newPaymentMethodData);

      if (response && response.data) {
        refetch();
        setNewPaymentMethod(defaultPaymentMethod);
        setErrors({ name: "", remarks: "" });
      } else {
        alert("エラーが発生しました。");
      }
    } catch (error) {
      if (error.response && error.response.status === 422) {
        const fieldErrors = error.response.data.message;
        setErrors((prev) => ({
          ...prev,
          ...fieldErrors,
        }));
      }
      else {
        alert("エラーが発生しました。");
      }
    }
  };

  const handleUpdateToggleCardPayment = () => {
    setEditPaymentMethod((prevData) => ({
      ...prevData,
      is_card_payment: !prevData.is_card_payment,
    }));
  }

  const handleUpdateToggleIsColorRed = () => {
    setEditPaymentMethod((prevData) => ({
      ...prevData,
      is_color_red: !prevData.is_color_red,
    }));
  }

  const handleUpdatePaymentMethod = async (updatedPaymentMethod) => {
    try {
      const { id, name, is_card_payment, is_color_red, remarks } = updatedPaymentMethod;
      const updatedData = {
        name,
        is_card_payment,
        is_color_red,
        remarks,
      };

      const response = await updatePaymentMethod(id, updatedData);

      if (response && response.data) {
        refetch();
        return true;
      } else {
        alert("エラーが発生しました。");
        return false;
      }
    } catch (error) {
      if (error.response && error.response.status === 422) {
        const fieldErrors = error.response.data.message;
        setErrorsUpdate((prev) => ({
          ...prev,
          ...fieldErrors,
        }));
      }
      else {
        alert("エラーが発生しました。");
      }
      return false;
    }
  };

  const handleDeletePaymentMethod = async (id) => {
    try {
      const response = await deletePaymentMethod(id);

      if (response.success) {
        refetch();
      } else {
        alert("エラーが発生しました。");
      }
    } catch (error) {
      alert("エラーが発生しました。");
    }
  };

  const handleRowsPerPageChange = (event) => {
    const newRowsPerPage = parseInt(event.target.value);
    const maxPage = Math.floor(total / newRowsPerPage);
    setRowsPerPage(newRowsPerPage);
    setPage(Math.min(page, maxPage));
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleFocus = (field) => {
    setFocusState((prevState) => ({
      ...prevState,
      [field]: true,
    }));
  };

  const handleBlur = (field) => {
    setFocusState((prevState) => ({
      ...prevState,
      [field]: false,
    }));
  };

  const handleEditClick = (paymentMethod) => {
    setEditPaymentMethod({
      ...paymentMethod,
      is_card_payment: paymentMethod.is_card_payment === IS_CARD_PAYMENT_TEXT,
    });
    setIsPopupOpenUpdate(true);
  };

  const handleDeleteClick = (paymentMethod) => {
    setPaymentMethodToDelete(paymentMethod);
    setIsPopupOpenDelete(true);
  }

  const handleClosePopup = () => {
    setIsPopupOpenUpdate(false);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setEditPaymentMethod((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    setErrorsUpdate({ name: "", remarks: "" });
  };

  return {
    arrPaymentMethods,
    activeSortColumn,
    sortColumnDirection,
    sortedData,
    newPaymentMethod,
    rowsPerPage,
    page,
    total,
    editPaymentMethod,
    paymentMethodToDelete,
    isPopupOpenUpdate,
    isPopupOpenDelete,
    focusState,
    nameInputRef,
    errors,
    errorsUpdate,
    setIsPopupOpenDelete,
    handleRowsPerPageChange,
    handlePageChange,
    handleSort,
    handleInputChange,
    handleAddPaymentMethod,
    handleUpdatePaymentMethod,
    handleDeletePaymentMethod,
    handleFocus,
    handleBlur,
    handleEditClick,
    handleDeleteClick,
    handleClosePopup,
    handleChange,
    handleUpdateToggleCardPayment,
    handleToggleCardPayment,
    handleToggleIsColorRed,
    handleUpdateToggleIsColorRed,
  };
};

export default usePaymentMethodMaster;
